import { mintCaptchaToken } from './captcha';
import {
    RuntimeFormError,
    type FormErrorCode,
    type FormField,
    type SubmitInput,
    type WireField,
} from './types';
import { uploadFiles } from './upload';

const STATUS_CODES: Record<number, FormErrorCode> = {
    // This script validates its own payload before sending, so a 400 here is the endpoint rejecting
    // the captcha token — stale (single-use, ~2 minutes) or judged a bot. Both are worth retrying.
    400: 'CAPTCHA_FAILED',
    // Runtime: not from a domain owning this alias. Preview: the injected token was rejected, usually
    // past its TTL — a reload fixes that, but nothing the visitor does in place will.
    403: 'UNKNOWN',
    404: 'FORM_NOT_FOUND',
    429: 'RATE_LIMITED',
};

/**
 * A generated site has no server-side page entity, so the owner notification would read
 * "on the  of your website". The route is the only page identity the browser has.
 */
const pageName = (): string => {
    const segment = window.location.pathname.split('/').filter(Boolean).pop();
    if (!segment) {
        return 'Home';
    }
    const words = decodeURIComponent(segment)
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/[-_]+/g, ' ')
        .trim();
    if (!words) {
        return 'Home';
    }
    return words.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const errorFor = async (response: Response): Promise<RuntimeFormError> => {
    const code = STATUS_CODES[response.status] ?? 'UNKNOWN';
    return new RuntimeFormError(
        response.status,
        code,
        await messageFrom(response),
    );
};

const messageFrom = async (response: Response): Promise<string> => {
    try {
        const body = (await response.json()) as { message?: string };
        if (body?.message) {
            return body.message;
        }
    } catch {
        // A non-JSON body (gateway error page, empty 429) is normal.
    }
    return `form submission failed with status ${response.status}`;
};

/**
 * Turns the caller's fields into the wire's all-strings shape, uploading every file the form
 * carries in one call so the whole submission spends a single rate-limiter token. Keys are
 * URL-encoded because they travel comma separated, and the server splits a file field on commas.
 */
const toWireFields = async (
    fields: FormField[],
    siteAlias: string,
): Promise<WireField[]> => {
    const files = fields.flatMap((field) =>
        field.type === 'file' ? field.value : [],
    );
    const keys = files.length > 0 ? await uploadFiles(siteAlias, files) : [];

    let taken = 0;
    return fields.map((field) => {
        if (field.type !== 'file') {
            return {
                name: field.name,
                label: field.label,
                type: field.type,
                value: field.value,
            };
        }
        const mine = keys.slice(taken, taken + field.value.length);
        taken += field.value.length;
        return {
            name: field.name,
            label: field.label,
            type: field.type,
            value: mine.map(encodeURIComponent).join(','),
        };
    });
};

// The prefix is the flow: the server reads designtime vs runtime from it, not from the payload.
const endpoint = (siteAlias: string, preview: boolean): string => {
    const alias = encodeURIComponent(siteAlias);
    return preview
        ? `/ms/vibe-code/public/forms/${alias}/submit`
        : `/rts/vibe-code/public/forms/${alias}/submit`;
};

export const submit = async (input: SubmitInput): Promise<void> => {
    const formId = typeof input?.formId === 'string' ? input.formId.trim() : '';
    if (!formId) {
        throw new RuntimeFormError(
            0,
            'BAD_REQUEST',
            'submit() requires a non-empty formId',
        );
    }
    const fields = Array.isArray(input?.fields) ? input.fields : [];
    if (!fields.length) {
        throw new RuntimeFormError(
            0,
            'BAD_REQUEST',
            'submit() requires at least one field',
        );
    }

    const siteAlias = window.Parameters?.SiteAlias;
    if (!siteAlias) {
        throw new RuntimeFormError(
            0,
            'UNKNOWN',
            'the page is missing its site alias',
        );
    }
    const siteKey = window.vibe?.config?.captchaSiteKey;
    if (!siteKey) {
        throw new RuntimeFormError(
            0,
            'UNKNOWN',
            'the page is missing its captcha site key',
        );
    }

    const preview = window.Parameters?.isRuntimeServer !== true;
    const previewToken = window.vibe?.config?.previewToken;
    if (preview && !previewToken) {
        throw new RuntimeFormError(
            0,
            'PREVIEW_UNAVAILABLE',
            'this preview cannot submit forms: the page is missing its preview token',
        );
    }

    // Uploaded before the token is minted: a token is single-use and short-lived, and a large file
    // over a slow connection would outlive it.
    const wireFields = await toWireFields(fields, siteAlias);

    const captchaToken = await mintCaptchaToken(siteKey);

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (previewToken && preview) {
        headers.Authorization = `Bearer ${previewToken}`;
    }

    let response: Response;
    try {
        response = await fetch(endpoint(siteAlias, preview), {
            method: 'POST',
            headers,
            body: JSON.stringify({
                formId,
                pageName: pageName(),
                fields: wireFields,
                captchaToken,
            }),
        });
    } catch {
        throw new RuntimeFormError(
            0,
            'NETWORK',
            'form submission could not reach the server',
        );
    }

    if (!response.ok) {
        throw await errorFor(response);
    }
};
